<template>
  <div>
    <div class="my-2 text-xs font-medium">{{ label }}</div>
    <template v-if="type === FieldType.OPTIONS">
      <div class="flex flex-wrap">
        <div
          v-for="field in fields"
          :key="`dynamic-offers-${field.offer_id}-field-${fieldName}`"
          :class="
            field.offer_id === offer.id
              ? 'border-transparent bg-primary/10 text-primary'
              : 'border-primary/10 text-gray-500'
          "
          class="cursor-pointer space-x-2 border px-4 py-2 text-xs font-medium select-none"
          @click="updateField(field.offer_id)"
        >
          <span>
            {{ convertValue(field.offer_id) }}
          </span>
        </div>
      </div>
    </template>
    <template v-else>
      <vue-slider
        v-bind="sliderProps"
        v-model="activeOfferId"
        class="mt-6"
        tooltip="always"
        data-value="offer_id"
        data-label="value"
        marks
        :data="fields"
        @change="updateField"
      >
        <template #tooltip="{ pos, value }">
          <div
            class="slider-tooltip text-xs font-normal text-headline"
            :class="{ hidden: pos === 0 || pos === 100 }"
          >
            {{ convertValue(value) }}
          </div>
        </template>
        <template #mark="{ value, pos, active, label }">
          <div
            class="slider-mark"
            :style="{ left: `${pos}%` }"
            :class="{ active }"
          >
            <div
              v-if="pos === 0 || pos === 100"
              class="slider-label text-gray-secondary text-xs font-normal"
              :data-value="value"
              :data-label="label"
              :class="[
                pos === 0 ? 'slider-label-left' : 'slider-label-right',
                { active },
              ]"
            >
              {{ convertValue(value) }}
            </div>
            <div v-else></div>
            <div class="slider-step" :class="{ active }"></div>
          </div>
        </template>
      </vue-slider>
    </template>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import vueSlider from "vue-3-slider-component";

import { FieldType } from "@/enums/offers";
import { formatMoney } from "@/helpers/formatting";

import type { OfferTemplateDetail, OfferView } from "@/models/offers";

const props = defineProps<{
  offer: OfferView;
  fields: (OfferTemplateDetail & { offer_id: string })[];
  fieldName: string;
}>();

const emit = defineEmits<{
  updateField: [string];
}>();

const sliderProps = {
  dotStyle: {
    backgroundColor: "white",
    borderColor: "#3769D6",
    borderWidth: "4px",
    width: "17px",
    height: "17px",
  },
  railStyle: {
    backgroundColor: "#EBEEF5",
    height: "9px",
  },
  processStyle: {
    backgroundColor: "#3769D6",
    height: "9px",
  },
};

const activeOfferId = computed(() => props.offer.id);
const label = computed(
  () =>
    props.offer.details.find(({ name }) => name === props.fieldName)?.label ||
    props.fieldName
);
const type = computed(
  () => props.offer.details.find(({ name }) => name === props.fieldName)?.type
);

const updateField = (offerId: string) => {
  emit("updateField", offerId);
};

const convertValue = (offerId: string) => {
  const field = props.fields.find(({ offer_id }) => offer_id === offerId);
  if (!field) {
    return "-";
  }

  if (type.value === FieldType.OPTIONS) {
    return field.options?.[field.value] ?? "-";
  }

  return (
    (type.value === FieldType.CURRENCY
      ? formatMoney(field.value)
      : field.value) || "-"
  );
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.slider-mark {
  position: absolute;
  width: 0;
  height: 100%;
  top: 50%;
  z-index: 4;
  transform: translate(-50%, -50%);
}
.slider-step {
  width: 5px;
  height: 5px;
  border-radius: 100%;
  background-color: #272a40;
  opacity: 32%;
  margin-top: 2px;
}
.slider-mark:first-child .slider-step {
  margin-left: 5px;
}
.slider-mark:last-child .slider-step {
  margin-left: -5px;
}
.slider-step.active {
  background-color: #ffffff;
}
.slider-label {
  margin-top: -7px;
  position: absolute;
}
.slider-label.slider-label-left {
  transform: translate(0%, -100%);
}
.slider-label.slider-label-right {
  transform: translate(-100%, -100%);
}
.slider-tooltip {
  left: 50%;
  transform: translate(-50%, -65%);
  visibility: visible;
  position: absolute;
}
</style>
